import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getAlreadyExistsError, getBooleanField, getTextField } from '@lender/utils';
import { Button } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import UploadDocumentsModal from 'app/shared/components/document/UploadDocumentsModal';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import useEditorLocationDescriptor from 'app/shared/components/forms/FormEditor/hooks/useEditorLocationDescriptor';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { FeatureFlag } from 'constants/featureFlags';
import { FormType, FormViewerNavigationType } from 'constants/forms';
import {
  disclosureTypeOptions,
  formApplicationTypeOptions,
  formPurposeTypeOptions,
} from 'constants/options';
import { PAGE_CODES } from 'constants/pageCodes';
import useForm from 'hooks/useForm';
import {
  formApplicationTypeConverter,
  getFormPurposeOptions,
  getFormPurposeSelectLabel,
  getFormPurposeTypeByPurpose,
  getGeographicalState,
  getIsFormNonOverridable,
  getOverrideBadge,
} from 'utils/forms';
import { jsonStringify } from 'utils/json';

import { getIsFromInternalCompany, getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  createFormOverrideAction,
  deleteFormAction,
  getFormNewApiTemplateAction,
  replaceFormTemplateContentAction,
  updateFormAction,
} from 'store/forms/actions';
import { selectFormNavigationType, selectFormsListByState } from 'store/forms/selectors';
import { closeDialogAction, openConfirmationDialogAction } from 'store/global/actions';
import { selectDocumentTypesOptions } from 'store/system/selectors';

import { formsTestIds } from '../../forms.testIds';

import {
  ContentActionText,
  getResetOverrideConfirmationDescription,
  REPLACE_CONTENT_CONFIRMATION_TITLE,
} from './EditFormModal.constants';
import { uploadDocumentsCustomStyles } from './EditFormModal.styles';
import {
  getFormOverrideTemplateType,
  getIsFormContentReplaceable,
  getIsFormNameValid,
  getReplaceContentConfirmationDescription,
  getSubmitButtonText,
  getUploadButtonText,
} from './EditFormModal.utils';

const schema = object().shape({
  name: getTextField().test('alreadyExist', getAlreadyExistsError('Form'), function () {
    return this.options.context?.isNameValid;
  }),
  documentType: getTextField(),
  formApplicationType: getTextField(),
  withPageNumbers: getBooleanField({ isRequired: false }),
  isUseCurrentFile: getBooleanField({ isRequired: false }),
  isUploadPdf: getBooleanField({ isRequired: false }),
});

const EditFormModal = ({ initialData, isDisabled = false }) => {
  const { push } = useHistory();
  const dispatch = useDispatch();

  const navigationType = useSelector(selectFormNavigationType);
  const isNavigationRequired = navigationType === FormViewerNavigationType.URL;
  const editorLocationDescriptor = useEditorLocationDescriptor();

  const documentTypeOptions = useSelector(selectDocumentTypesOptions);
  const stateForms = useSelector(selectFormsListByState(initialData.state));
  const stateFormsExceptCurrent = useMemo(
    () => stateForms.filter(({ formId }) => formId !== initialData.formId),
    [stateForms, initialData.formId],
  );

  const isInternalCompany = useSelector(getIsFromInternalCompany);
  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.FORMS));
  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isGlobalForm = checkIsInternalCompany({ companyId: initialData.companyId });
  const { hasLocalOverride, isCustom, formType } = initialData;

  const isOverrideFlow =
    isOverrideEnabled &&
    !isInternalCompany &&
    isGlobalForm &&
    !getIsFormNonOverridable({ formType, isCustom });
  const isOverrideBadgeVisible = isOverrideEnabled && !isInternalCompany;
  const isCanCreateOverride = isOverrideFlow && !hasLocalOverride;
  const isPdf = formType === FormType.PDF;
  const isResetOverrideVisible = isManageAllowed && isOverrideBadgeVisible && hasLocalOverride;
  const isContentReplaceAllowed =
    isManageAllowed &&
    getIsFormContentReplaceable({
      formType,
      isCustom,
      isGlobalForm,
      isInternalCompany,
      isOverrideFlow,
      hasLocalOverride,
    });
  const isContentOptionsVisible = isCanCreateOverride || isContentReplaceAllowed;

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const isFieldDisabled = isFormSubmitting || isDisabled;
  const isOverridableFieldDisabled = isFieldDisabled || isOverrideFlow;

  const overrideBadge = useMemo(
    () => getOverrideBadge({ isGlobal: isGlobalForm, hasLocalOverride }),
    [isGlobalForm, hasLocalOverride],
  );

  const abortController = useMemo(() => new AbortController(), []);

  const initialDocumentType = useMemo(() => {
    if (!initialData.documentTypeId) {
      return documentTypeOptions[0];
    }

    // A form can reference a document type that has since been deleted — it is absent from the
    // active options, so `find` returns undefined. Coerce to null so the required-field validation
    // forces the user to pick a valid type instead of crashing on a missing selection.
    return documentTypeOptions.find(({ value }) => value === initialData.documentTypeId) ?? null;
  }, [documentTypeOptions, initialData.documentTypeId]);

  const initialPurposeTypeValue =
    initialData.purposeType ?? getFormPurposeTypeByPurpose(initialData.formPurpose);

  const {
    form,
    isFormChanged,
    setSchema,
    validationErrors,
    handleChange,
    setInForm,
    setMultipleInForm,
    validateField,
    validateForm,
  } = useForm({
    name: initialData.name,
    documentType: initialDocumentType,
    isRequired: false,
    formApplicationType: formApplicationTypeOptions.find(
      ({ value }) => value === initialData.formApplicationType,
    ),
    withPageNumbers: initialData.withPageNumbers,
    disclosureType:
      disclosureTypeOptions.find(({ value }) => value === initialData.disclosureType) || null,
    purposeType:
      formPurposeTypeOptions.find(({ value }) => value === initialPurposeTypeValue) || null,
    formPurpose:
      getFormPurposeOptions(initialPurposeTypeValue).find(
        ({ value }) => value === initialData.formPurpose,
      ) || null,
    isUseCurrentFile: true,
    isUploadPdf: true,
  });

  const purposeTypeValue = form.purposeType?.value ?? null;
  const formPurposeOptions = getFormPurposeOptions(purposeTypeValue);
  const formPurposeLabel = getFormPurposeSelectLabel(purposeTypeValue);

  const isPdfUploadVisible = isContentOptionsVisible && isPdf && !form.isUseCurrentFile;
  const isContentReplaced = isContentReplaceAllowed && !form.isUseCurrentFile;

  const formDataToSend = useMemo(
    () => ({
      formId: initialData.formId,
      name: form.name,
      geographicalState: getGeographicalState(initialData),
      type: formApplicationTypeConverter(form.formApplicationType?.value),
      documentTypeId: form.documentType?.value,
      withPageNumbers: form.withPageNumbers,
      disclosureType: form.disclosureType?.value || null,
      purposeType: form.purposeType?.value || null,
      formPurpose: form.formPurpose?.value || null,
    }),
    [
      initialData,
      form.name,
      form.formApplicationType,
      form.documentType?.value,
      form.withPageNumbers,
      form.disclosureType,
      form.purposeType,
      form.formPurpose,
    ],
  );

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onNavigate = useCallback(
    ({ formId, state, formType }) => {
      FormsPaths.viewForm.navigateWith(
        push,
        {
          [FORMS_ROUTING_PARAMS.area]: state,
          [FORMS_ROUTING_PARAMS.formId]: formId,
          [FORMS_ROUTING_PARAMS.formType]: formType,
          [FORMS_ROUTING_PARAMS.selectedPage]: 1,
        },
        editorLocationDescriptor,
      );
    },
    [push, editorLocationDescriptor],
  );

  const onNameChange = useCallback(
    e => {
      const isNameValid = getIsFormNameValid({
        name: e.target.value,
        forms: stateFormsExceptCurrent,
      });
      handleChange(e, { isNameValid });
    },
    [handleChange, stateFormsExceptCurrent],
  );

  const onSubmitSuccess = useCallback(
    payload => {
      dispatch(closeDialogAction());
      dispatch(getFormNewApiTemplateAction({ formId: initialData.formId }));

      if (isNavigationRequired && payload) {
        onNavigate(payload);
      }
    },
    [dispatch, initialData.formId, isNavigationRequired, onNavigate],
  );

  const createFormOverride = useCallback(
    (file = null) => {
      setIsFormSubmitting(true);

      const formData = new FormData();
      formData.append(
        'body',
        jsonStringify({
          formId: initialData.formId,
          formOverrideTemplate: {
            type: getFormOverrideTemplateType({
              isUseCurrentFile: form.isUseCurrentFile,
              isFileUploaded: !!file && isPdf,
            }),
          },
          withPageNumbers: form.withPageNumbers,
        }),
      );

      if (file) {
        formData.append('file', file);
      }

      return dispatch(createFormOverrideAction({ formId: initialData.formId, formData }))
        .then(({ payload }) => onSubmitSuccess(payload))
        .catch(Error)
        .finally(() => setIsFormSubmitting(false));
    },
    [
      setIsFormSubmitting,
      initialData.formId,
      form.isUseCurrentFile,
      form.withPageNumbers,
      isPdf,
      dispatch,
      onSubmitSuccess,
    ],
  );

  const validateFormName = useCallback(
    () =>
      validateForm({
        isNameValid: getIsFormNameValid({ name: form.name, forms: stateFormsExceptCurrent }),
      }),
    [validateForm, form.name, stateFormsExceptCurrent],
  );

  const confirmContentReplace = useCallback(
    isFileUploaded =>
      new Promise(resolve => {
        dispatch(
          openConfirmationDialogAction({
            type: ConfirmationModalType.WARNING,
            message: REPLACE_CONTENT_CONFIRMATION_TITLE,
            description: getReplaceContentConfirmationDescription({
              isPdf,
              isFileUploaded,
              isAppliedToAllCompanies: isGlobalForm && isInternalCompany,
            }),
            onAcceptButtonName: ContentActionText.REPLACE,
            onAccept: () => resolve(true),
            onCancel: () => resolve(false),
          }),
        );
      }),
    [dispatch, isPdf, isGlobalForm, isInternalCompany],
  );

  const replaceFormContent = useCallback(
    async (file = null) => {
      if (!(await validateFormName()) || !(await confirmContentReplace(!!file))) {
        return null;
      }

      setIsFormSubmitting(true);

      return dispatch(updateFormAction(formDataToSend))
        .then(() =>
          dispatch(replaceFormTemplateContentAction({ formId: initialData.formId, file })),
        )
        .then(({ payload }) => onSubmitSuccess(payload))
        .catch(Error)
        .finally(() => setIsFormSubmitting(false));
    },
    [
      validateFormName,
      confirmContentReplace,
      setIsFormSubmitting,
      dispatch,
      initialData.formId,
      formDataToSend,
      onSubmitSuccess,
    ],
  );

  const uploadFile = useCallback(
    file => (isCanCreateOverride ? createFormOverride(file) : replaceFormContent(file)),
    [isCanCreateOverride, createFormOverride, replaceFormContent],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (isCanCreateOverride) {
        createFormOverride();

        return;
      }

      if (isContentReplaced) {
        replaceFormContent();

        return;
      }

      if (!(await validateFormName())) {
        return;
      }

      setIsFormSubmitting(true);
      dispatch(updateFormAction(formDataToSend))
        .then(({ payload }) => onSubmitSuccess(payload))
        .catch(Error)
        .finally(() => setIsFormSubmitting(false));
    },
    [
      isCanCreateOverride,
      createFormOverride,
      isContentReplaced,
      replaceFormContent,
      validateFormName,
      setIsFormSubmitting,
      dispatch,
      formDataToSend,
      onSubmitSuccess,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onPurposeTypeChange = useCallback(
    (e, value) => {
      setMultipleInForm({ purposeType: value || null, formPurpose: null });
    },
    [setMultipleInForm],
  );

  const onResetOverride = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Reset to original',
        description: getResetOverrideConfirmationDescription(initialData.name),
        onAccept: () => {
          setIsFormSubmitting(true);

          dispatch(
            deleteFormAction({
              formId: initialData.formId,
              message: 'Form reset to original',
            }),
          )
            .then(() => dispatch(getFormNewApiTemplateAction({ formId: initialData.formId })))
            .then(() => dispatch(closeDialogAction()))
            .catch(Error)
            .finally(() => setIsFormSubmitting(false));
        },
      }),
    );
  }, [dispatch, initialData.name, initialData.formId, setIsFormSubmitting]);

  return (
    <div className="w-full">
      <form
        noValidate
        className="flex flex-col justify-center"
        data-ischanged={isFormChanged}
        onSubmit={onSubmit}
      >
        {isOverrideBadgeVisible && (
          <div className="mb-24">
            <LabelCDS
              size={LabelSize.M}
              title={overrideBadge.title}
              variant={overrideBadge.variant}
            />
          </div>
        )}

        <TextFieldCDS
          autoFocus
          fullWidth
          className="mb-24"
          data-testid={formsTestIds.FORMS_EDIT_FORM_NAME_INPUT}
          disabled={isOverridableFieldDisabled}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
          id="formName"
          label="Form Name"
          maxLength={150}
          name="name"
          value={form.name}
          variant="outlined"
          onChange={onNameChange}
        />

        <AutocompleteCDS
          containerClassName="mb-24"
          dataTestId={formsTestIds.FORMS_EDIT_FORM_FORMS_EDIT_FORM_AUTOCOMPLETE_SELECT}
          dataTestIdInput={formsTestIds.FORMS_EDIT_FORM_FORMS_EDIT_FORM_AUTOCOMPLETE_INPUT}
          disabled={isOverridableFieldDisabled}
          error={!!validationErrors.documentType}
          helperText={validationErrors.documentType}
          label="Document Type"
          name="documentType"
          options={documentTypeOptions}
          value={form.documentType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          disableClearable
          containerClassName="mb-24"
          data-testid={formsTestIds.FORMS_EDIT_FORM_FORM_APPLICATION_TYPE_SELECT}
          disabled={isOverridableFieldDisabled}
          error={!!validationErrors.formApplicationType}
          helperText={validationErrors.formApplicationType}
          label="Form Type"
          name="formApplicationType"
          options={formApplicationTypeOptions}
          value={form.formApplicationType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          containerClassName="mb-24"
          dataTestId={formsTestIds.FORMS_EDIT_FORM_DISCLOSURE_TYPE_SELECT}
          disabled={isOverridableFieldDisabled}
          label="Disclosure Type"
          name="disclosureType"
          options={disclosureTypeOptions}
          value={form.disclosureType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          containerClassName="mb-24"
          dataTestId={formsTestIds.FORMS_EDIT_FORM_PURPOSE_TYPE_SELECT}
          disabled={isOverridableFieldDisabled}
          label="Form Purpose"
          name="purposeType"
          options={formPurposeTypeOptions}
          value={form.purposeType}
          onChange={onPurposeTypeChange}
        />

        <AutocompleteCDS
          containerClassName="mb-24"
          dataTestId={formsTestIds.FORMS_EDIT_FORM_FORM_PURPOSE_SELECT}
          disabled={isOverridableFieldDisabled}
          isHidden={!purposeTypeValue}
          label={formPurposeLabel}
          name="formPurpose"
          options={formPurposeOptions}
          value={form.formPurpose}
          onChange={onAutocompleteChange}
        />

        <CheckboxCDS
          fullWidth
          data-testid={formsTestIds.WITH_PAGE_NUMBERS_CHECKBOX}
          disabled={isFieldDisabled}
          error={!!validationErrors.withPageNumbers}
          helperText={validationErrors.withPageNumbers}
          label="With Page Numbers"
          name="withPageNumbers"
          value={form.withPageNumbers}
          onChange={handleChange}
        />

        {isContentOptionsVisible && (
          <div className="mb-24">
            <CheckboxCDS
              fullWidth
              data-testid={formsTestIds.FORMS_EDIT_FORM_USE_CURRENT_FILE_CHECKBOX}
              disabled={isFormSubmitting}
              label="Use Current File"
              name="isUseCurrentFile"
              value={form.isUseCurrentFile}
              onChange={handleChange}
            />

            {isPdfUploadVisible && (
              <>
                <CheckboxCDS
                  fullWidth
                  data-testid={formsTestIds.FORMS_EDIT_FORM_IS_UPLOAD_PDF_CHECKBOX}
                  disabled={isFormSubmitting}
                  error={!!validationErrors.isUploadPdf}
                  helperText={validationErrors.isUploadPdf}
                  label="Upload a PDF"
                  name="isUploadPdf"
                  value={form.isUploadPdf}
                  onChange={handleChange}
                />
                {form.isUploadPdf && (
                  <UploadDocumentsModal
                    abortController={abortController}
                    customStyles={uploadDocumentsCustomStyles}
                    isModal={false}
                    isMultiple={false}
                    uploadButtonText={getUploadButtonText({ isCanCreateOverride })}
                    uploadFile={uploadFile}
                  />
                )}
              </>
            )}
          </div>
        )}

        {(!isPdfUploadVisible || !form.isUploadPdf) && (
          <div className="flex justify-end">
            {isResetOverrideVisible && (
              <Button
                className="whitespace-nowrap"
                data-testid={formsTestIds.FORMS_EDIT_FORM_RESET_OVERRIDE_BUTTON}
                disabled={isFormSubmitting}
                variant="outlined"
                onClick={onResetOverride}
              >
                Reset to original
              </Button>
            )}
            <Button
              className="whitespace-nowrap ml-8"
              data-testid={formsTestIds.FORMS_EDIT_FORM_SUBMIT_BUTTON}
              disabled={(!isFormChanged && !isCanCreateOverride) || isFormSubmitting}
              type="submit"
              variant="contained"
            >
              {getSubmitButtonText({ isCanCreateOverride, isContentReplaced })}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

EditFormModal.propTypes = {
  ...WithLoadingPropTypes,
  initialData: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
};

export default compose(withLoading, memo)(EditFormModal);
